<template>
	<div class="transactions-table-filters">
		<section v-if="showClassificationFilters" class="filters">
			<h5>{{ t("filters.classification-title") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="assetTypes"
					:options="assetTypeSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.asset-type')"
					:placeholder="$t('filters.all-masculine')"
					:same-width="true"
				/>
			</div>

			<div class="content filters">
				<ArkListboxDropdown
					v-model="landCategories"
					:options="landCategorySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.land-category')"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableLandCategoryFilter"
					:same-width="true"
				/>
			</div>

			<div class="content filters">
				<ArkListboxDropdown
					v-model="landSubcategories"
					:options="landSubcategorySelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.land-subcategory')"
					:placeholder="$t('filters.all-feminine')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableSubcategoryFilter"
					:same-width="true"
				/>
			</div>
		</section>

		<section v-if="store.currentUser?.has(Permission.TransactionRead)" class="filters">
			<h5>{{ t("filters.transaction") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="transactionVerificationStatus"
					:options="transactionVerificationStatusSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.transaction-verification-status-title')"
					:placeholder="$t('filters.all-masculine')"
					:same-width="true"
				/>
			</div>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="transactionBuyerStatus"
					:options="buyerStatusSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.buyer-status-title')"
					:placeholder="$t('filters.all-masculine')"
					:same-width="true"
				/>
			</div>
			<div v-if="store.currentUser?.has(Permission.TransactionWrite)" class="content filters">
				<ArkListboxDropdown
					v-model="transactionSource"
					:options="transactionSourceSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.transaction-source-title')"
					:placeholder="$t('filters.all-feminine')"
					:same-width="true"
				/>
			</div>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="transactionComposition"
					:options="transactionCompositionSelectOptions"
					:multiple="false"
					:clearable="true"
					:searchable="false"
					:label="$t('filters.transaction-composition-title')"
					:placeholder="$t('filters.all-feminine')"
					:same-width="true"
				/>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("filters.others-title") }}</h5>
			<div class="content range-filters">
				<div class="range superficy">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="superficyRange"
							:label="$t('opportunities.unitFields.superficy')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[10000, 50000, 250000]"
						/>
					</div>
				</div>

				<div class="range constructible">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="constructibleRange"
							:label="$t('opportunities.unitFields.constructible')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[15000, 50000, 200000]"
						/>
					</div>
				</div>

				<div class="range price">
					<div class="values">
						<RangeNotchedSlider
							v-model="priceRange"
							:label="`${$t('transactions-table.columns.purchase-price')} ($)`"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[250000, 1000000, 5000000]"
							unit-prefix="$"
						/>
					</div>
				</div>

				<div class="range residential-units">
					<div class="values">
						<RangeNotchedSlider
							v-model="residentialUnitsRange"
							:label="$t('opportunities.residentialUnits')"
							:min="residentialUnitsSliderMin"
							:max="residentialUnitsSliderMax"
							:thresholds="[15, 40, 100]"
						/>
					</div>
				</div>
			</div>
		</section>

		<section class="filters">
			<h5>{{ t("opportunities.locationAndType") }}</h5>
			<div class="content filters">
				<ArkListboxDropdown
					v-model="regions"
					:options="regionSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.regions-title')"
					:placeholder="$t('opportunities.all-regions')"
					:searchNormalizer="normalizeLabel"
					:same-width="true"
				/>

				<ArkListboxDropdown
					v-model="boroughs"
					:options="boroughSelectOptions"
					:multiple="true"
					:clearable="true"
					:searchable="true"
					:label="$t('filters.boroughs-title')"
					:placeholder="$t('opportunities.all-boroughs')"
					:searchNormalizer="normalizeLabel"
					:disabled="shouldDisableBoroughFilter"
					:same-width="true"
				/>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRaw, watch, nextTick } from "vue"
import { useI18n } from "vue-i18n"
import { config, Feature } from "@/AppConfig.ts"
import AreaRangeNotchedSlider from "@/components/ui/AreaRangeNotchedSlider.vue"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import { RANGE_SLIDER_MAX, RESIDENTIAL_UNITS_SLIDER_MAX, RESIDENTIAL_UNITS_SLIDER_MIN } from "@/components/constants"
import RangeNotchedSlider from "@/components/ui/RangeNotchedSlider.vue"
import { useView } from "@/composables/useView"
import { AssetType, LandCategory, LandSubcategory } from "@/graphql"
import {
	type BoroughRecord,
	type Municipality,
	TransactionComposition,
	TransactionMetadataBuyerStatus,
	TransactionSource,
} from "@/model/DataModel.ts"
import { Permission, UseRootStore } from "@/model/RootStore"
import { TransactionVerificationStatus, ViewScope } from "@/types"

const { t } = useI18n()
const store = UseRootStore()
const view = useView(ViewScope.TransactionsTable)

const assetTypes = ref<string[]>([])
const landCategories = ref<string[]>([])
const landSubcategories = ref<string[]>([])
const transactionVerificationStatus = ref<TransactionVerificationStatus | null>(null)
const transactionBuyerStatus = ref<string | null>(null)
const transactionSource = ref<string | null>(null)
const transactionComposition = ref<string | null>(null)
const superficyRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const constructibleRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const priceRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])
const residentialUnitsSliderMin = RESIDENTIAL_UNITS_SLIDER_MIN
const residentialUnitsSliderMax = RESIDENTIAL_UNITS_SLIDER_MAX
const residentialUnitsRange = ref<[number, number]>([residentialUnitsSliderMin, residentialUnitsSliderMax])
const regions = ref<string[]>([])
const boroughs = ref<string[]>([])
let isApplyingViewFilter = false

const buyerStatusSelectOptions = computed(() =>
	Object.values(TransactionMetadataBuyerStatus).map(value => ({
		value,
		label: t(`filters.buyer-status-${value}`),
	}))
)

const transactionVerificationStatusSelectOptions = computed(() => [
	{ value: TransactionVerificationStatus.Verified, label: t("filters.transaction-verification-status-verified") },
	{ value: TransactionVerificationStatus.Unverified, label: t("filters.transaction-verification-status-unverified") },
])

const transactionSourceSelectOptions = computed(() =>
	(Object.values(TransactionSource) as string[]).map(value => ({
		value,
		label: t(`filters.transaction-source-${value}`),
	}))
)

const transactionCompositionSelectOptions = computed(() =>
	(Object.values(TransactionComposition) as string[]).map(value => ({
		value,
		label: t(`filters.transaction-composition-${value}`),
	}))
)

// regions / municipalities / boroughs -> multi (options come from real reference data, selection is local)
const regionSelectOptions = computed(() => regionItems.value.map(o => ({ value: o.id, label: o.label })))

const boroughSelectOptions = computed(() => boroughItems.value.map(o => ({ value: o.id, label: o.label })))

const selectedRegions = computed(() => new Set<string>(regions.value))

const shouldDisableBoroughFilter = computed(() => selectedRegions.value.size === 0)

const availableRegionNames = ref<string[]>([])
const availableMunicipalityRecords = ref<Municipality[]>([])
const availableBoroughRecords = ref<BoroughRecord[]>([])

// classification filters
const showClassificationFilters = computed(() => config.ui.has(Feature.DevTypes))
const shouldDisableLandCategoryFilter = computed(() => {
	if (!assetTypes.value.length) return false
	return !assetTypes.value.includes(AssetType.Land)
})

const shouldShowLandCategoryFilter = computed(() => {
	if (!assetTypes.value.length) return true // Show by default
	return assetTypes.value.includes(AssetType.Land)
})

const shouldShowSubcategoryFilter = computed(() => {
	if (!shouldShowLandCategoryFilter.value) return false
	if (!landCategories.value.length) return false

	const relevantCategories = [
		LandCategory.Residential,
		LandCategory.Commercial,
		LandCategory.Industrial,
		LandCategory.Agricultural,
	]
	return landCategories.value.some((cat: any) => relevantCategories.includes(cat))
})

const shouldDisableSubcategoryFilter = computed(() => {
	if (shouldDisableLandCategoryFilter.value) return true
	if (!landCategories.value.length) return true

	const relevantCategories = [
		LandCategory.Residential,
		LandCategory.Commercial,
		LandCategory.Industrial,
		LandCategory.Agricultural,
	]
	return !landCategories.value.some((cat: any) => relevantCategories.includes(cat))
})

const assetTypeSelectOptions = computed(() => [
	...Object.values(AssetType).map(value => ({
		value,
		label: t(`property.assetType.${value}`),
	})),
	{ value: "none", label: t("property.assetType.none") },
])

const landCategorySelectOptions = computed(() =>
	Object.values(LandCategory).map(value => ({
		value,
		label: t(`property.landCategory.${value}`),
	}))
)

const landSubcategorySelectOptions = computed(() => {
	if (!landCategories.value.length) return []

	const options = []

	// Residential
	if (landCategories.value.includes(LandCategory.Residential)) {
		options.push(
			{ value: LandSubcategory.LowRise, label: t("property.landSubcategory.LowRise") },
			{ value: LandSubcategory.MidRise, label: t("property.landSubcategory.MidRise") },
			{ value: LandSubcategory.HighRise, label: t("property.landSubcategory.HighRise") },
			{ value: LandSubcategory.TownHouse, label: t("property.landSubcategory.TownHouse") },
			{ value: LandSubcategory.SingleFamily, label: t("property.landSubcategory.SingleFamily") }
		)
	}

	// Commercial
	if (landCategories.value.includes(LandCategory.Commercial)) {
		options.push(
			{ value: LandSubcategory.LowRise, label: t("property.landSubcategory.LowRise") },
			{ value: LandSubcategory.MidRise, label: t("property.landSubcategory.MidRise") },
			{ value: LandSubcategory.HighRise, label: t("property.landSubcategory.HighRise") }
		)
	}

	// Industrial
	if (landCategories.value.includes(LandCategory.Industrial)) {
		options.push(
			{ value: LandSubcategory.LightMedium, label: t("property.landSubcategory.LightMedium") },
			{ value: LandSubcategory.Heavy, label: t("property.landSubcategory.Heavy") }
		)
	}

	// Agricultural
	if (landCategories.value.includes(LandCategory.Agricultural)) {
		options.push(
			{ value: LandSubcategory.FullyCultivated, label: t("property.landSubcategory.FullyCultivated") },
			{ value: LandSubcategory.MostlyCultivated, label: t("property.landSubcategory.MostlyCultivated") },
			{ value: LandSubcategory.HalfAndHalf, label: t("property.landSubcategory.HalfAndHalf") },
			{ value: LandSubcategory.MostlyForestry, label: t("property.landSubcategory.MostlyForestry") },
			{ value: LandSubcategory.FullForestry, label: t("property.landSubcategory.FullForestry") }
		)
	}

	const seen = new Set()
	return options.filter(option => {
		if (seen.has(option.value)) return false
		seen.add(option.value)
		return true
	})
})

onMounted(async () => {
	const [regionNames, municipalityRecords, loadedBoroughs] = await Promise.all([
		store.getRegions,
		store.getMunicipalityRecords,
		store.getBoroughRecords,
	])
	availableRegionNames.value = regionNames
	availableMunicipalityRecords.value = municipalityRecords
	availableBoroughRecords.value = loadedBoroughs
})

watch(landCategories, () => {
	if (!shouldShowSubcategoryFilter.value) {
		landSubcategories.value = []
		return
	}

	// Keep only valid subcategories
	if (landSubcategories.value.length) {
		const validValues: string[] = landSubcategorySelectOptions.value.map(o => o.value)
		landSubcategories.value = landSubcategories.value.filter(sub => validValues.includes(sub))
	}
})

// Normalize a label by removing accents and lowercasing it
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
function normalizeLabel(name: string) {
	return name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
}

const regionItems = computed(() =>
	toRaw(availableRegionNames.value).map(name => ({
		id: name,
		label: name,
	}))
)

const hasMunicipalityRegionData = computed(() =>
	availableMunicipalityRecords.value.some(municipality => municipality.regionName)
)

// municipality names within the selected regions, used to cascade the borough filter directly
// off regions now that the municipality filter itself lives in the toolbar quick filters
const municipalityNamesInSelectedRegions = computed(() => {
	const municipalityRecords = toRaw(availableMunicipalityRecords.value)
	const filteredMunicipalities = hasMunicipalityRegionData.value
		? municipalityRecords.filter(
				municipality => municipality.regionName && selectedRegions.value.has(municipality.regionName)
			)
		: municipalityRecords

	return new Set(filteredMunicipalities.map(municipality => municipality.name))
})

const boroughItems = computed(() =>
	toRaw(availableBoroughRecords.value)
		.filter(
			borough => borough.municipalityName && municipalityNamesInSelectedRegions.value.has(borough.municipalityName)
		)
		.map(borough => ({
			id: borough.name,
			label: borough.name,
		}))
		.sort((left, right) => left.label.localeCompare(right.label))
)

function filterValuesByOptions(values: string[], options: { value: string }[]): string[] {
	const validValues = new Set(options.map(option => option.value))
	return values.filter(value => validValues.has(value))
}

watch([regions, () => boroughSelectOptions.value.map(option => option.value)], () => {
	boroughs.value = shouldDisableBoroughFilter.value
		? []
		: filterValuesByOptions(boroughs.value, boroughSelectOptions.value)
})

function rangeValue(
	range: { min?: number | null; max?: number | null } | undefined,
	defaultRange: [number, number]
): [number, number] {
	return [range?.min ?? defaultRange[0], range?.max ?? defaultRange[1]]
}

function syncFilterValue<T>(source: { value: T }, update: (value: T) => void): void {
	watch(
		source,
		value => {
			if (isApplyingViewFilter) return
			update(value)
		},
		{ deep: true }
	)
}

syncFilterValue(assetTypes, view.setTransactionAssetTypes)
syncFilterValue(landCategories, view.setTransactionLandCategories)
syncFilterValue(landSubcategories, view.setTransactionLandSubcategories)
syncFilterValue(transactionVerificationStatus, view.setTransactionVerificationStatus)
syncFilterValue(transactionBuyerStatus, view.setTransactionBuyerStatus)
syncFilterValue(transactionSource, view.setTransactionSource)
syncFilterValue(transactionComposition, view.setTransactionComposition)
syncFilterValue(superficyRange, view.setTransactionLandAreaRange)
syncFilterValue(constructibleRange, view.setTransactionBuildableLandAreaRange)
syncFilterValue(priceRange, view.setTransactionPriceRange)
syncFilterValue(residentialUnitsRange, view.setTransactionResidentialUnitsRange)
syncFilterValue(regions, view.setTransactionRegions)
syncFilterValue(boroughs, view.setTransactionBoroughs)

watch(
	() => view.view.value.filter?.transaction,
	filter => {
		isApplyingViewFilter = true
		assetTypes.value = filter?.assetTypes ?? []
		landCategories.value = filter?.landCategories ?? []
		landSubcategories.value = filter?.landSubcategories ?? []
		transactionVerificationStatus.value = filter?.verificationStatus ?? null
		transactionBuyerStatus.value = filter?.buyerStatus ?? null
		transactionSource.value = filter?.source ?? null
		transactionComposition.value = filter?.composition ?? null
		superficyRange.value = rangeValue(filter?.landArea, [0, RANGE_SLIDER_MAX])
		constructibleRange.value = rangeValue(filter?.buildableLandArea, [0, RANGE_SLIDER_MAX])
		priceRange.value = rangeValue(filter?.price, [0, RANGE_SLIDER_MAX])
		residentialUnitsRange.value = rangeValue(filter?.residentialUnits, [
			residentialUnitsSliderMin,
			residentialUnitsSliderMax,
		])
		regions.value = filter?.regions ?? []
		boroughs.value = filter?.boroughs ?? []
		nextTick(() => {
			isApplyingViewFilter = false
		})
	},
	{ deep: true, immediate: true }
)

const resetFilters = () => {
	assetTypes.value = []
	landCategories.value = []
	landSubcategories.value = []
	transactionVerificationStatus.value = null
	transactionBuyerStatus.value = null
	transactionSource.value = null
	transactionComposition.value = null
	superficyRange.value = [0, RANGE_SLIDER_MAX]
	constructibleRange.value = [0, RANGE_SLIDER_MAX]
	priceRange.value = [0, RANGE_SLIDER_MAX]
	residentialUnitsRange.value = [residentialUnitsSliderMin, residentialUnitsSliderMax]
	regions.value = []
	boroughs.value = []
	view.resetFilter()
}

const activeFiltersCount = computed(() => {
	const isRangeActive = (range: [number, number], [min, max]: [number, number]) => range[0] !== min || range[1] !== max

	return [
		assetTypes.value.length > 0,
		landCategories.value.length > 0,
		landSubcategories.value.length > 0,
		transactionVerificationStatus.value != null,
		transactionBuyerStatus.value != null,
		transactionSource.value != null,
		transactionComposition.value != null,
		isRangeActive(superficyRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(constructibleRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(priceRange.value, [0, RANGE_SLIDER_MAX]),
		isRangeActive(residentialUnitsRange.value, [residentialUnitsSliderMin, residentialUnitsSliderMax]),
		regions.value.length > 0,
		boroughs.value.length > 0,
	].filter(Boolean).length
})

function hasActiveFilters(): boolean {
	return activeFiltersCount.value > 0
}

defineExpose({ resetFilters, hasActiveFilters, activeFiltersCount })
</script>

<style scoped>
.transactions-table-filters {
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);

	section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		&:not(:last-child) {
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
			padding-bottom: var(--spacing-lg);
		}
	}

	h5 {
		margin: 0 0 var(--spacing-xs);
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		letter-spacing: 0.02em;
	}

	.content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
	}

	.range-filters {
		padding: 0;
		padding-bottom: var(--spacing-lg);

		.range {
			--input-width: var(--width-input-field);

			.values {
				margin-top: var(--spacing-sm);
			}
		}
	}

	.filters {
		min-height: var(--spacing-2xl);
		font-size: var(--font-size-small);
		width: 100%;
		position: relative;
	}
}
</style>
